import React from 'react';
import PropTypes from 'prop-types';
import { Validators } from '../../data/Validators';
import UIHelper from '../../utils/UIHelper';
import ASSET_TYPES from '../../types/AssetTypes';
import mui from '../../utils/MuiIcons';
import Thumbnail from '../../components/Thumbnail';
import LastAction from './LastAction';

/**
 * @typedef {import('../../types/User').User} User
 * @typedef {import('../../types/ContentItems').ContentItem} ContentItem
 * @typedef {import('../../types/Playlist').PlaylistResponse} Playlist
 * @typedef {import('../../data/ValidateResult').ValidateResult} ValidateResult
 */

/**
 * Renders a single asset row in the asset selection table.
 * Displays asset details including thumbnail, type, name, duration, metadata, and an add button.
 * @param {Object} props - The component props.
 * @param {ContentItem} props.item - The asset item object containing id, type, name, etc.
 * @param {boolean} props.isSelected - Indicates if the asset is currently selected.
 * @param {Function} props.onClick - Callback function invoked when the add button is clicked, passing the item.
 * @param {LastAction} [props.lastAction] - The result of the last selection action.
 * @returns {JSX.Element} The rendered table row element.
 */
function AssetTableRow({ item, isSelected, onClick, lastAction }) {
  const rowClass = `${isSelected ? 'is-selected' : ''}`;

  /**
   * Returns validation text if the asset is invalid.
   * @returns {ValidateResult}
   */
  const validate = () => {
    if (item.type === ASSET_TYPES.playlist) {
      /**
       * @type {Playlist} item
       */
      const playlist = item;
      const playlistItemsResult = Validators.isNotEmpty('Playlist items', playlist.files);

      if (!playlistItemsResult.isValid) {
        return playlistItemsResult;
      }
    }

    if (item.type === ASSET_TYPES.image) {
      return Validators.successValidate('Image asset');
    }

    return Validators.validateAsset(item);
  };

  const validationResult = validate();
  /**
   * Returns the name and metadata display for the asset.
   * @returns {JSX.Element} The name div with title, folder, and tags.
   */
  const getName = () => <div>{UIHelper.asset.getAssetNameEle(item, 'asset-table-row', true)}</div>;

  /**
   * Returns the formatted duration display if available.
   * @returns {JSX.Element|null} The duration div or null.
   */
  const getDuration = () => {
    const { duration } = item;
    const dur = UIHelper.time.formatDuration(duration);

    return <div>{dur.raw === 0 ? '' : dur.formatted}</div>;
  };

  /**
   * Returns the metadata display for the asset.
   * @returns {JSX.Element} Folder, tags, and validity details for the asset.
   */
  const getMetaData = () => (
    <div>
      <div className="undertext">Folder: {item.parent || '/'}</div>
      {item.tags && item.tags.length > 0 && (
        <div className="undertext">Tags: {item.tags.join(', ')}</div>
      )}
      {Boolean(item.validityEnabled) && (
        <p className="undertext darkblue">
          Validity: {UIHelper.time.formatDate(item.validityStart)}
          {' - '}
          {UIHelper.time.formatDate(item.validityEnd)}
        </p>
      )}
    </div>
  );
  /**
   * Returns the asset type icon element.
   * @returns {JSX.Element}
   */
  const getTypeIcon = () => (
    <span className={`asset-table-asset-type ${mui.className}`} title={`${item.type}`}>
      {mui.icons[item.type]}
    </span>
  );
  /**
   * Returns the resolution display, including a warning icon if there is a resolution mismatch.
   * @returns {JSX.Element}
   */
  const getResolution = () => (
    <div className="asset-table-resolution">
      {item.resolutionMismatch ? (
        <span
          className={`${mui.className} icon-warning`}
          title="Resolution mismatch"
          data-test="resolution-warning"
        >
          {mui.icons.warning_mark}
        </span>
      ) : null}
      {UIHelper.asset.formatResolution(item.width, item.height)}
    </div>
  );

  /**
   * Returns the action button element, which is an add button if the asset is valid, or a warning icon if invalid.
   * The add button changes appearance based on whether the asset was just added or is a duplicate.
   * @returns {JSX.Element}
   */
  const getActionButton = () => {
    const showAdded = lastAction.isJustAdded(item.id, item.type);
    const showDuplicate = lastAction.isDuplicate(item.id, item.type);
    let btnClass = `${mui.className} orange-button`;
    let btnIcon = mui.icons.add;

    if (showAdded) {
      btnClass = `${mui.className} success-button`;
      btnIcon = mui.icons.check_mark;
    } else if (showDuplicate) {
      btnClass = `${mui.className} warning-button`;
      btnIcon = mui.icons.warning_duplicate;
    }

    return (
      <div>
        {validationResult.isValid ? (
          <button
            data-test="asset-table-row-add-button"
            type="button"
            className={btnClass}
            onClick={handleAddClick}
            aria-label="Add asset"
            title={showDuplicate ? 'Already added' : ''}
          >
            {btnIcon}
          </button>
        ) : (
          <span
            className={`${mui.className} warning`}
            title={validationResult.message}
            data-test="asset-table-row-add-button"
          >
            {mui.icons.warning_mark}
          </span>
        )}
      </div>
    );
  };

  /**
   * Returns the thumbnail element for the asset.
   * @returns {JSX.Element}
   */
  const getThumbnail = () => (
    <div className="asset-table-thumbnail">
      <Thumbnail asset={item} />
    </div>
  );

  /**
   * Handles the add button click, calling onClick with item.
   */
  const handleAddClick = () => {
    onClick(item);
  };

  return (
    <tr className={rowClass} data-id={item.id} data-type={item.type}>
      <td />
      <td>{getTypeIcon()}</td>
      <td>{getThumbnail()}</td>
      <td>{getName()}</td>
      <td>{getDuration()}</td>
      <td>{getResolution()}</td>
      <td>{getMetaData()}</td>
      <td>{getActionButton()}</td>
    </tr>
  );
}

AssetTableRow.propTypes = {
  item: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  lastAction: PropTypes.instanceOf(LastAction).isRequired,
};

export default AssetTableRow;
